import { ValidationError } from "sequelize"
import {
    RrsTemplate,
    RrsTemplateCreate,
    RrsTemplateSave,
    RrsTemplateChangeDefault,
    RrsTemplateSetProjectDefault,
    RrsTemplateRiskMapGenerated,
    RrsTemplateImport,
} from "server/models"
import { NONE, VALUE, UNLIMITED } from "const"
import { OrderType } from "client/utils"
import {
    GET, POST, DELETE, sort, search, validate,
} from "client/store/utils"
import {
    PackageSettings,
    UserData,
    LangType,
    RrsTemplateList,
    RrsTemplateValidation,
    RrsTemplateFilters,
} from "client/store"

export const LoadAllRrsTemplates = (accountId: number, projectId: number) => (
    GET(`/rrsTemplate/accountId/${accountId}/projectId/${projectId}`)
    .then((templates: RrsTemplate[]) => {
        const templateList: RrsTemplateList = {}
        templates.forEach((template) => {
            templateList[template.id] = template
        })
        return templateList
    })
)

export const LoadRrsTemplateById = (templateId: number, accountId: number, projectId: number) => (
    GET(`/rrsTemplate/id/${templateId}/accountId/${accountId}/projectId/${projectId}`)
    .then((template: RrsTemplate) => template)
)

type SaveRrsTemplateResponse = {
    newTemplateId: number
    validation: RrsTemplateValidation
}
export const SaveRrsTemplate = (
    id: number,
    template: RrsTemplateCreate,
    maxTemplateNumber: number,
    lang: LangType,
) => (
    LoadAllRrsTemplates(template.accountId, template.projectId).then((templateList) => {
        if (!id && maxTemplateNumber !== null && Object.keys(templateList).length >= maxTemplateNumber) {
            return <SaveRrsTemplateResponse>{
                newTemplateId: null,
                validation: <RrsTemplateValidation>{ name: lang.maxRrsTemplatesReached.replace("{0}", maxTemplateNumber.toString()) },
            }
        }

        const data: RrsTemplateSave = {
            id,
            template,
        }
        return POST(
            "/rrsTemplate/save",
            JSON.stringify(data),
        ).then((res) => {
            const newTemplate = res as RrsTemplate
            const error = res as ValidationError

            return <SaveRrsTemplateResponse>{
                newTemplateId: newTemplate.id || null,
                validation: <RrsTemplateValidation>validate(error, lang),
            }
        })
    })
)

export const ChangeDefaultRrsTemplate = (template: RrsTemplate) => {
    const isDefault: boolean = !template.default
    const data: RrsTemplateChangeDefault = {
        id: template.id,
        isDefault,
    }
    return POST(
        "/rrsTemplate/changeDefault",
        JSON.stringify(data),
    ).then((updatedRows: [number]) => (
        updatedRows && updatedRows.length && updatedRows[0] === 1
            ? isDefault
            : template.default
    ))
}

export const SetDefaultProjectRrsTemplate = (templateId: number, projectId: number) => {
    const data: RrsTemplateSetProjectDefault = {
        id: templateId,
        projectId,
    }
    return POST(
        "/rrsTemplate/setProjectDefault",
        JSON.stringify(data),
    )
}

export const SetRrsTemplateRiskMapGenerated = (templateIds: number[], generated: boolean) => {
    const data: RrsTemplateRiskMapGenerated = {
        templateIds,
        generated,
    }
    return POST(
        "/rrsTemplate/riskMapGenerated",
        JSON.stringify(data),
    )
}

export const DeleteRrsTemplate = (templateId: number, accountId: number, projectId: number) => (
    DELETE(`/rrsTemplate/delete/${templateId}/${accountId}/${projectId}`)
    .then((removedRows: number) => removedRows)
)

export const ImportRrsTemplates = (data: RrsTemplateImport) => (
    POST(
        "/rrsTemplate/import",
        JSON.stringify(data),
    )
)

export const CopyDefaultRrsTemplates = (
    accountId: number,
    projectId: number,
    defaultTemplatesAssigned: boolean,
) => (
    GET(`/rrsTemplate/default/accountId/${defaultTemplatesAssigned ? accountId : 1}`)
    .then((templates: RrsTemplate[]) => {
        const data: RrsTemplateImport = {
            templates,
            accountId,
            projectId,
        }
        return ImportRrsTemplates(data)
    })
)

export const SortRrsTemplate = (
    templates: RrsTemplate[],
    sortColumn: keyof RrsTemplate,
    sortOrder: OrderType,
    filters: RrsTemplateFilters,
): number[] => {
    const templatesFiltered = templates.filter((template) => {
        let showRow = true
        if (filters.search) showRow = search(filters.search, template)
        return showRow
    })

    const templateIds: number[] = []
    templatesFiltered.sort((a, b) => sort(a, b, sortColumn, sortOrder))
    templatesFiltered.forEach((template) => templateIds.push(template.id))

    return templateIds
}

export const GetMaxRrsTemplates = (
    packageSettings: PackageSettings,
    userData: UserData,
    templatesNumber?: number,
    lang?: LangType,
) => {
    const { rrsRiskSpaceTemplates, rrsRiskSpaceTemplatesValue } = packageSettings

    let maxTemplates = 0
    if (rrsRiskSpaceTemplates === NONE) maxTemplates = 0
    if (rrsRiskSpaceTemplates === VALUE && rrsRiskSpaceTemplatesValue) maxTemplates = rrsRiskSpaceTemplatesValue
    if (rrsRiskSpaceTemplates === UNLIMITED) maxTemplates = null
    if (userData.accountId === 1) maxTemplates = null

    // feedbacks
    let feedback: string = ""
    if (lang) {
        if (maxTemplates === null) {
            feedback = lang.maxRrsTemplatesUnlimited
        } else {
            if (templatesNumber < maxTemplates) feedback = lang.maxRrsTemplates
            if (templatesNumber === maxTemplates) feedback = lang.maxRrsTemplatesReached
            if (templatesNumber > maxTemplates) feedback = lang.maxRrsTemplatesExceeded
            feedback = feedback.replace("{0}", maxTemplates.toString())
        }
    }

    return {
        maxTemplates,
        feedback,
    }
}

export const IsLockedRrsTemplate = (
    projectId: number,
    template: RrsTemplate,
) => (
    projectId ? template && template.locked : false
)
